import React, { useState, useEffect } from 'react';
import {
  nameStyle, frameProps, auraStyle, RARITY_COLORS,
} from './cosmetics';
import './ArenaShop.css';

const BASE = 'https://fotileams.com/api';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const CHAR_BASE = 'https://fotileams.com/characters';
const PREVIEW_CHEF = 'chefu-ember.webp'; // neutral chef for previews

const TIER_META = {
  starter: { label: 'Starter',  sub: 'Your first flames — earn these fast', icon: '🌱' },
  mid:     { label: 'Ascendant', sub: 'For the consistent — a week or two of fire', icon: '⚔️' },
  elite:   { label: 'Legendary', sub: 'The pinnacle — only the dedicated wear these', icon: '👑' },
};

const CATEGORY_LABEL = {
  aura: 'Aura', frame: 'Frame', namecolor: 'Name', effect: 'Effect', title: 'Title', badge: 'Badge',
};

// Renders a small preview of a single cosmetic on a mini Chefu card.
function CosmeticPreview({ item }) {
  const cos = { render_type: item.render_type, asset: item.asset };
  const aura  = auraStyle(cos);
  const frame = item.category === 'frame' ? frameProps(cos) : { className: '', style: {} };
  const nStyle = (item.category === 'namecolor') ? nameStyle(cos) : {};

  // Particle effects
  const particle = (item.render_type === 'css_particle') ? item.asset
                 : (item.render_type === 'css_effect' && item.asset === 'sparks') ? 'sparks'
                 : null;
  const isHalo = item.render_type === 'css_effect' && item.asset === 'halo';

  const PARTICLE_CHAR = {
    snow: '❄', embers: '•', electric: '⚡', petals: '🌸',
    stars: '✦', sparks: '•', bubbles: '○',
  };
  const pchar = PARTICLE_CHAR[particle] || '•';

  return (
    <div className={`shop-preview ${frame.className}`} style={frame.style}>
      {aura && <div className="shop-preview-aura" style={aura} />}
      {isHalo && <div className="shop-preview-halo" />}
      {particle && (
        <div className={`shop-particles shop-particles-${particle}`}>
          <span>{pchar}</span><span>{pchar}</span><span>{pchar}</span>
          <span>{pchar}</span><span>{pchar}</span>
        </div>
      )}
      <img className="shop-preview-img" src={`${CHAR_BASE}/${PREVIEW_CHEF}`} alt="" />
      <div className="shop-preview-name" style={nStyle}>
        {item.category === 'title' ? item.asset
          : item.category === 'badge' ? <>Chefu {item.asset}</>
          : 'Chefu'}
      </div>
    </div>
  );
}

export default function ArenaShop({ onClose }) {
  const [coins, setCoins]     = useState(0);
  const [level, setLevel]     = useState(1);
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(null);
  const [toast, setToast]     = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE}/league/shop`, { headers: authHeaders() });
      const data = await res.json();
      setCoins(data.coins || 0);
      setLevel(data.level || 1);
      setItems(data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const buy = async (item) => {
    setBusy(item.id);
    try {
      const res = await fetch(`${BASE}/league/buy`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ item_id: item.id }),
      });
      const data = await res.json();
      if (data.error) { showToast(data.error); return; }
      showToast(`✨ ${item.name} unlocked!`);
      await load();
    } catch { showToast('Something went wrong'); }
    finally { setBusy(null); }
  };

  const equip = async (item, equipped) => {
    setBusy(item.id);
    try {
      const res = await fetch(`${BASE}/league/equip`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ item_id: item.id, equipped }),
      });
      const data = await res.json();
      if (data.error) { showToast(data.error); return; }
      showToast(equipped ? `Equipped ${item.name}` : `Removed ${item.name}`);
      await load();
    } catch { showToast('Something went wrong'); }
    finally { setBusy(null); }
  };

  const tiers = ['starter', 'mid', 'elite'];
  const byTier = (t) => items.filter(i => i.tier === t);

  return (
    <div className="shop-root">
      {toast && <div className="shop-toast">{toast}</div>}

      {/* Header with coin balance */}
      <div className="shop-header">
        <button className="shop-back" onClick={onClose}>← Arena</button>
        <div className="shop-title">Flame Shop</div>
        <div className="shop-coins">
          <span className="shop-coin-icon">🪙</span>
          <span className="shop-coin-num">{coins.toLocaleString()}</span>
        </div>
      </div>

      <div className="shop-intro">
        Spend coins earned from your on-time streaks on cosmetics. They change how
        your Chefu looks to <b>everyone</b> on the leaderboard — pure flex, no shortcuts.
      </div>

      {loading ? (
        <div className="shop-loading"><div className="shop-spinner">🔥</div>Loading the shop…</div>
      ) : (
        tiers.map(t => {
          const list = byTier(t);
          if (list.length === 0) return null;
          const meta = TIER_META[t];
          return (
            <div key={t} className="shop-tier">
              <div className="shop-tier-head">
                <span className="shop-tier-icon">{meta.icon}</span>
                <div>
                  <div className="shop-tier-label">{meta.label}</div>
                  <div className="shop-tier-sub">{meta.sub}</div>
                </div>
              </div>

              <div className="shop-grid">
                {list.map(item => {
                  const rar = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
                  const locked = !item.unlocked;               // below required level
                  const canBuy = item.unlocked && item.affordable && !item.owned;
                  return (
                    <div
                      key={item.id}
                      className={`shop-card ${item.owned ? 'owned' : ''} ${locked ? 'locked' : ''}`}
                      style={{ '--rar': rar.color, '--rar-glow': rar.glow }}
                    >
                      <div className="shop-card-rarity">{rar.label}</div>

                      <CosmeticPreview item={item} />

                      <div className="shop-card-name">{item.name}</div>
                      <div className="shop-card-cat">{CATEGORY_LABEL[item.category]}</div>
                      <div className="shop-card-desc">{item.description}</div>

                      {/* Action zone */}
                      {item.owned ? (
                        <button
                          className={`shop-btn ${item.equipped ? 'shop-btn-equipped' : 'shop-btn-equip'}`}
                          disabled={busy === item.id}
                          onClick={() => equip(item, !item.equipped)}
                        >
                          {item.equipped ? '✓ Equipped' : 'Equip'}
                        </button>
                      ) : locked ? (
                        <div className="shop-locked-tag">🔒 Reach Level {item.min_level}</div>
                      ) : (
                        <button
                          className={`shop-btn ${canBuy ? 'shop-btn-buy' : 'shop-btn-cant'}`}
                          disabled={!canBuy || busy === item.id}
                          onClick={() => buy(item)}
                        >
                          <span className="shop-price-coin">🪙</span>
                          {item.price_coins.toLocaleString()}
                          {!item.affordable && <span className="shop-need"> — need more</span>}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <div className="shop-foot">🔥 Keep your streak alive — every on-time day fills your coin purse.</div>
    </div>
  );
}